// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ResyncLeaveGuard } from '@/components/resync-leave-guard';
import {
  CheckCircleOutlined as CheckCircleOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  SyncOutlined as SyncOutlinedIcon,
  WarningStatusIcon,
} from '@/constants/hardfin-standard-icons';
import { useMutationWithGlobalErrorHandler } from '@/hooks/use-mutation-with-global-error-handler';
import { useResyncCountdown } from '@/hooks/use-resync-countdown';
import { useResyncErrorToast } from '@/hooks/use-resync-error-toast';
import { useResyncEstimate } from '@/shipment/hooks/use-resync-estimate';
import type { ResyncOrderType } from '@/shipment/hooks/use-resync-estimate';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { MAX_MANUAL_RESYNC_SECONDS } from '@/shipment/utilities/estimate-resync-time';
import { format3plSyncTimestamp } from '@/utilities/format-3pl-sync-timestamp';
import { formatDuration } from '@/utilities/format-duration';
import { pluralize } from '@/utilities/pluralize';
import {
  Button,
  ClickAwayListener,
  Divider,
  Fade,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';

// Delay before a hover-opened surface closes on mouse-out, so moving from the
// signal into the surface does not dismiss it.
const HOVER_CLOSE_DELAY_MS = 120;

interface Props {
  // Which order flavor is resyncing; drives the shipment-based estimate filter
  orderType: ResyncOrderType;
  orderKey: string;
  // The order number, used to estimate resync time from its shipments
  orderNumber: string;
  // True once the order is terminal (received, cancelled, or closed); the icon
  // greys out and the "next check" line is hidden
  isTerminal: boolean;
  // True while the order is still awaiting 3PL activity ("Sent to 3PL"); once
  // activity begins (or the order is terminal) the label reads "Synced from 3PL"
  isAwaiting: boolean;
  // Hardfin's last poll of the 3PL for this order
  lastSyncedAt: DateTime | null;
  // The 3PL's last reported data update for this order
  lastThirdPartyUpdateAt: DateTime | null;
  // Whether the 3PL binding is currently in an error state
  hasSyncError: boolean;
  // The latest 3PL sync error text (empty when there is none)
  syncErrorMessage: string;
  // Open quarantined serials on this order. Quarantine is a customer decision,
  // not a sync failure: the binding syncs clean and the signal shows a warning
  // tier distinct from the error state.
  quarantineCount?: number;
  // Domain resync API call; the resolved order is written back to detailQueryKey
  resyncOrder: (orderKey: string) => Promise<unknown>;
  detailQueryKey: QueryKey;
  listQueryKey: QueryKey;
}

/**
 * 3PL sync-status signal shared by purchase orders, return orders, and
 * fulfillment orders. Shows "Sent to 3PL" while the order awaits 3PL activity
 * and "Synced from 3PL" afterwards; the icon greys out once the order is
 * terminal. Hovering opens a details surface like a tooltip; clicking pins it
 * open like a popover so the manual resync action can be used.
 */
export function OrderSyncSignal(props: Props) {
  const queryClient = useQueryClient();
  const handleResyncError = useResyncErrorToast();
  // Page-local guard that hides the manual action right after a forced resync, so
  // a user cannot hammer the 3PL. It resets on navigation or page refresh.
  const [hasForcedThisSession, setHasForcedThisSession] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  // A pinned (clicked) surface behaves like a popover: it ignores mouse-out and
  // closes only on click-away or Escape. An unpinned surface behaves like a tooltip.
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const openSurface = () => {
    clearCloseTimer();
    setOpen(true);
  };
  const closeSurface = () => {
    clearCloseTimer();
    setOpen(false);
    setPinned(false);
  };
  const scheduleHoverClose = () => {
    if (pinned) {
      return;
    }
    clearCloseTimer();
    closeTimerRef.current = setTimeout(
      () => setOpen(false),
      HOVER_CLOSE_DELAY_MS
    );
  };

  const resyncMutation = useMutationWithGlobalErrorHandler({
    mutationFn: () => props.resyncOrder(props.orderKey),
    errorMessage: 'We were unable to resync this order with the 3PL',
    onSuccess: (updatedOrder: unknown) => {
      queryClient.setQueryData(props.detailQueryKey, updatedOrder);
      queryClient.invalidateQueries({
        queryKey: props.listQueryKey,
        refetchType: 'active',
      });
      // A resync can create, revive, or update shipments, so refresh shipment data too
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
    },
    onError: (error: unknown) => {
      // Refetch the order so the persisted sync-error state (red button) updates.
      queryClient.invalidateQueries({ queryKey: props.detailQueryKey });
      // Show a specific toast for the known resync errors; otherwise re-throw so the
      // global support snackbar handles it.
      if (!handleResyncError(error)) {
        throw error;
      }
    },
  });

  const handleForceResync = () => {
    setHasForcedThisSession(true);
    closeSurface();
    resyncMutation.mutate();
  };

  const handleAnchorClick = () => {
    if (pinned) {
      closeSurface();
      return;
    }
    setPinned(true);
    openSurface();
  };

  const handleAnchorKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAnchorClick();
    } else if (event.key === 'Escape') {
      closeSurface();
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // The anchor manages its own toggle; ignore click-aways that land on it.
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }
    closeSurface();
  };

  // Estimate resync time from the order's shipments, fetched only while the surface is open or a
  // resync is running so the header stays cheap otherwise.
  const {
    estimateSeconds,
    isReady: isEstimateReady,
    isError: isEstimateError,
  } = useResyncEstimate(
    props.orderType,
    props.orderNumber,
    open || resyncMutation.isPending
  );
  // Very large orders would hold a request open too long, so manual resync is blocked and left
  // to the automatic sweeps.
  const resyncBlocked = estimateSeconds > MAX_MANUAL_RESYNC_SECONDS;

  // While a resync runs the full surface stays open with a live countdown; only the icon,
  // label, resync button, and estimate line change.
  const isResyncing = resyncMutation.isPending;
  const remainingSeconds = useResyncCountdown(isResyncing, estimateSeconds);
  let countdownText: string | null = null;
  if (isResyncing && estimateSeconds > 0) {
    countdownText =
      remainingSeconds > 0
        ? `About ${formatDuration(remainingSeconds, { short: true })} left`
        : 'Still working';
  }

  const quarantineCount = props.quarantineCount ?? 0;
  // Quarantine shows a warning tier between success and error: the sync itself
  // is clean, but open serials need a user decision before the order can move.
  const hasQuarantine = quarantineCount > 0;

  const label = isResyncing
    ? 'Resyncing now'
    : props.isAwaiting
      ? 'Sent to 3PL'
      : 'Synced from 3PL';
  const iconColor = props.hasSyncError
    ? 'error.main'
    : hasQuarantine
      ? 'warning.main'
      : props.isTerminal
        ? 'text.secondary'
        : 'success.main';

  // The tooltip-target icon: a reversed spinner while resyncing, an error icon
  // in error, a warning icon with open quarantine, else a check.
  const signalIcon = isResyncing ? (
    <SyncOutlinedIcon
      fontSize="small"
      sx={{
        color: 'text.secondary',
        '@keyframes hf-resync-spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(-360deg)' },
        },
        animation: 'hf-resync-spin 1s linear infinite',
      }}
    />
  ) : props.hasSyncError ? (
    <ErrorOutlineIcon fontSize="small" sx={{ color: iconColor }} />
  ) : hasQuarantine ? (
    <WarningStatusIcon fontSize="small" sx={{ color: iconColor }} />
  ) : (
    <CheckCircleOutlinedIcon fontSize="small" sx={{ color: iconColor }} />
  );

  // The button is red when the binding is in error, but the same once-per-page-load
  // guard still applies so the operator cannot spam retries. While resyncing it stays visible
  // (disabled).
  const showManualResync = isResyncing || !hasForcedThisSession;
  const hasSurfaceContent =
    isResyncing ||
    props.lastSyncedAt !== null ||
    props.lastThirdPartyUpdateAt !== null ||
    !props.isTerminal ||
    props.hasSyncError ||
    hasQuarantine ||
    showManualResync;

  if (!hasSurfaceContent) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {signalIcon}
        <Typography variant="body2">{label}</Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack
        ref={anchorRef}
        direction="row"
        spacing={0.5}
        alignItems="center"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        onMouseEnter={openSurface}
        onMouseLeave={scheduleHoverClose}
        onFocus={openSurface}
        onBlur={scheduleHoverClose}
        onClick={handleAnchorClick}
        onKeyDown={handleAnchorKeyDown}
        sx={{ cursor: 'pointer', display: 'inline-flex', width: 'fit-content' }}
      >
        {signalIcon}
        <Typography
          variant="body2"
          sx={{
            textDecoration: 'underline dotted',
            textUnderlineOffset: '3px',
          }}
        >
          {label}
        </Typography>
      </Stack>

      <Popper
        open={open || isResyncing}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        {(popperProps) => (
          <Fade {...popperProps.TransitionProps} timeout={150}>
            <Paper
              elevation={6}
              role="dialog"
              aria-label="3PL sync details"
              onMouseEnter={clearCloseTimer}
              onMouseLeave={scheduleHoverClose}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  closeSurface();
                }
              }}
              // Match the standard dark tooltip frame so the surface reads as a tooltip
              sx={{ maxWidth: 360, bgcolor: 'grey.700', color: 'common.white' }}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                <Stack spacing={0.5} sx={{ p: 1.5 }}>
                  {props.lastSyncedAt !== null && (
                    <Typography variant="caption">
                      Last Hardfin check for 3PL data:{' '}
                      {format3plSyncTimestamp(props.lastSyncedAt)}
                    </Typography>
                  )}
                  {props.lastThirdPartyUpdateAt !== null && (
                    <Typography variant="caption">
                      Last 3PL data update:{' '}
                      {format3plSyncTimestamp(props.lastThirdPartyUpdateAt)}
                    </Typography>
                  )}
                  {props.hasSyncError ? (
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ErrorOutlineIcon
                          fontSize="small"
                          sx={{ color: 'common.white' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: 'common.white', fontWeight: 'bold' }}
                        >
                          Your 3PL data has a problem:
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        component="ul"
                        sx={{ color: 'common.white', m: 0, pl: 2.5 }}
                      >
                        <li>{props.syncErrorMessage || 'Unknown error'}</li>
                      </Typography>
                    </Stack>
                  ) : (
                    !props.isTerminal && (
                      <Typography variant="caption">
                        Next check in 5 minutes
                      </Typography>
                    )
                  )}
                  {hasQuarantine && (
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <WarningStatusIcon
                          fontSize="small"
                          sx={{ color: 'common.white' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: 'common.white', fontWeight: 'bold' }}
                        >
                          {pluralize(quarantineCount, {
                            singular: 'serial is in quarantine',
                            plural: 'serials are in quarantine',
                          })}
                        </Typography>
                      </Stack>
                      <Typography variant="caption">
                        The 3PL reported these serials under a conflicting item,
                        so the affected shipment cannot proceed. Resolve each
                        serial on the held shipment's quarantine panel.
                      </Typography>
                    </Stack>
                  )}
                  {showManualResync && (
                    <>
                      <Divider
                        sx={{
                          my: 0.5,
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        }}
                      />
                      {isResyncing ? (
                        <>
                          <Button
                            variant="text"
                            size="small"
                            disabled
                            startIcon={<SyncOutlinedIcon fontSize="small" />}
                            sx={{
                              alignSelf: 'flex-start',
                              textTransform: 'none',
                              '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.5)',
                              },
                            }}
                          >
                            {props.hasSyncError
                              ? 'Attempt to resync manually now'
                              : 'Force resync manually now'}
                          </Button>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          >
                            {countdownText && (
                              <>
                                {countdownText}
                                <br />
                              </>
                            )}
                            <b>You must stay on this page while resyncing</b>
                          </Typography>
                        </>
                      ) : resyncBlocked ? (
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          This order cannot be manually resynced, it will be
                          retried at midnight automatically
                        </Typography>
                      ) : isEstimateError ? (
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          The resync estimate failed to load, so manual resync
                          is unavailable — reload the page to try again
                        </Typography>
                      ) : (
                        <>
                          <Button
                            variant="text"
                            size="small"
                            // Disabled until the estimate loads, so a large order cannot be
                            // manually resynced before we know it exceeds the cap.
                            disabled={!isEstimateReady}
                            startIcon={<SyncOutlinedIcon fontSize="small" />}
                            onClick={handleForceResync}
                            sx={{
                              alignSelf: 'flex-start',
                              textTransform: 'none',
                              color: 'common.white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.5)',
                              },
                            }}
                          >
                            {props.hasSyncError
                              ? 'Attempt to resync manually now'
                              : 'Force resync manually now'}
                          </Button>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          >
                            {estimateSeconds > 0 && (
                              <>
                                Estimated resync time:{' '}
                                {formatDuration(estimateSeconds, {
                                  short: true,
                                })}
                                <br />
                              </>
                            )}
                            <b>You must stay on this page while resyncing</b>
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
      <ResyncLeaveGuard active={isResyncing} />
    </>
  );
}
